import { FC, ReactNode } from 'react'
import { Box, Divider, Stack, Typography } from '@mui/material'

import { CHART_CARD_TITLE_SX } from '@/common/styles'

/**
 * The header of a chart card, per Figma: the card's title on the left, its
 * controls against the right edge, and a rule separating the pair from the chart
 * below.
 *
 * The row wraps rather than crushing the controls on a narrow viewport, and
 * `children` are grouped in their own row so they keep an even gap between
 * themselves when a card carries more than one.
 */
const ChartCardHeader: FC<{
  /** Optional: a card whose header is controls only (see `MapTableToggleHeader`). */
  title?: ReactNode
  /** Heading level. Cards on the dashboard sit under a section heading, so they vary. */
  titleComponent?: 'h2' | 'h3'
  /** Rendered against the right edge — dropdowns, autocompletes, toggles, legends. */
  children?: ReactNode
}> = ({ title, titleComponent = 'h2', children }) => (
  <Box>
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      // Gap rather than margin so the wrapped rows are spaced too.
      useFlexGap
      sx={{ flexWrap: 'wrap', rowGap: 1, minHeight: 40 }}
    >
      {title && (
        <Typography variant="h5" component={titleComponent} sx={CHART_CARD_TITLE_SX}>
          {title}
        </Typography>
      )}
      {children && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          useFlexGap
          // `ml: auto` rather than `justifyContent: space-between` on the row: the
          // controls stay against the right edge even on a card with no title.
          sx={{ flexWrap: 'wrap', rowGap: 1, ml: 'auto' }}
        >
          {children}
        </Stack>
      )}
    </Stack>
    <Divider sx={{ mt: 1 }} />
  </Box>
)

export default ChartCardHeader
